import React, { Component } from 'react';
import { connect } from 'react-redux';
import PageHeader from '../../components/PageHeader';
import { apiCall } from '../../helpers/MainHelper';
import { toast } from 'react-toastify';
import ReactLoading from 'react-loading';
import Swal from 'sweetalert2';
import './VehicleBrandManagement.css';

class VehicleBrandManagement extends Component {
    state = {
        brands: [],
        statistics: {
            total_brands: 0,
            total_models: 0,
            models_with_images: 0,
            brands_with_logos: 0,
        },
        loading: true,
        searchQuery: '',
        showModal: false,
        modalType: '', // 'createBrand', 'updateBrand', 'createModel', 'updateModel'
        selectedBrand: null,
        selectedModel: null,
        formData: {
            name: '',
            img_url: '',
        },
        logoFile: null,
        logoPreview: null,
        removeLogo: false,
        expandedBrands: {},
        processingAction: false,
    };

    logoInputRef = React.createRef();

    componentDidMount() {
        window.scrollTo(0, 0);
        this.fetchBrands();
        this.fetchStatistics();
    }

    fetchBrands = async () => {
        try {
            this.setState({ loading: true });
            const response = await apiCall('admin/vehicle-brands/list', 'get');

            if (response.status === 'success') {
                this.setState({ brands: response.data || [], loading: false });
            } else {
                toast.error('Marka listesi yüklenemedi');
                this.setState({ loading: false });
            }
        } catch (error) {
            console.error('Error fetching brands:', error);
            toast.error('Bir hata oluştu: ' + (error.response?.data?.message || error.message));
            this.setState({ loading: false });
        }
    };

    fetchStatistics = async () => {
        try {
            const response = await apiCall('admin/vehicle-brands/statistics', 'get');

            if (response.status === 'success') {
                this.setState({ statistics: response.data });
            }
        } catch (error) {
            console.error('Error fetching statistics:', error);
        }
    };

    // Brand Modal Handlers
    handleOpenCreateBrandModal = () => {
        this.setState({
            showModal: true,
            modalType: 'createBrand',
            selectedBrand: null,
            formData: { name: '', img_url: '' },
            logoFile: null,
            logoPreview: null,
            removeLogo: false,
        });
    };

    handleOpenUpdateBrandModal = (brand) => {
        this.setState({
            showModal: true,
            modalType: 'updateBrand',
            selectedBrand: brand,
            formData: { name: brand.name || '', img_url: '' },
            logoFile: null,
            logoPreview: brand.logo_url || null,
            removeLogo: false,
        });
    };

    // Model Modal Handlers
    handleOpenCreateModelModal = (brand) => {
        this.setState({
            showModal: true,
            modalType: 'createModel',
            selectedBrand: brand,
            selectedModel: null,
            formData: { name: '', img_url: '' },
            logoFile: null,
            logoPreview: null,
            removeLogo: false,
        });
    };

    handleOpenUpdateModelModal = (brand, model) => {
        this.setState({
            showModal: true,
            modalType: 'updateModel',
            selectedBrand: brand,
            selectedModel: model,
            formData: { name: model.name || '', img_url: model.img_url || '' },
            logoFile: null,
            logoPreview: null,
            removeLogo: false,
        });
    };

    handleCloseModal = () => {
        this.setState({
            showModal: false,
            modalType: '',
            selectedBrand: null,
            selectedModel: null,
            formData: { name: '', img_url: '' },
            logoFile: null,
            logoPreview: null,
            removeLogo: false,
        });
    };

    handleInputChange = (e) => {
        const { name, value } = e.target;
        this.setState({
            formData: {
                ...this.state.formData,
                [name]: value,
            },
        });
    };

    handleLogoSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Dosya boyutu kontrolü (2MB)
            if (file.size > 2 * 1024 * 1024) {
                toast.error('Logo dosyası 2MB\'dan küçük olmalıdır');
                return;
            }

            // Dosya türü kontrolü
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                toast.error('Sadece JPG, PNG, SVG ve WebP dosyaları yüklenebilir');
                return;
            }

            // Preview oluştur
            const reader = new FileReader();
            reader.onloadend = () => {
                this.setState({
                    logoFile: file,
                    logoPreview: reader.result,
                    removeLogo: false,
                });
            };
            reader.readAsDataURL(file);
        }
    };

    handleRemoveLogo = () => {
        this.setState({
            logoFile: null,
            logoPreview: null,
            removeLogo: true,
        });
    };

    triggerLogoInput = () => {
        if (this.logoInputRef.current) {
            this.logoInputRef.current.click();
        }
    };

    handleSubmit = async (e) => {
        e.preventDefault();
        const { modalType, formData, selectedBrand, selectedModel, logoFile, removeLogo } = this.state;

        if (!formData.name || formData.name.trim() === '') {
            toast.error('Lütfen isim girin');
            return;
        }

        this.setState({ processingAction: true });

        try {
            let endpoint = '';
            let response;

            switch (modalType) {
                case 'createBrand':
                    endpoint = 'admin/vehicle-brands/create';
                    if (logoFile) {
                        // FormData ile logo yükle
                        const createFormData = new FormData();
                        createFormData.append('name', formData.name);
                        createFormData.append('logo', logoFile);
                        response = await apiCall(endpoint, 'post', createFormData);
                    } else {
                        response = await apiCall(endpoint, 'post', { name: formData.name });
                    }
                    break;

                case 'updateBrand':
                    endpoint = `admin/vehicle-brands/update/${selectedBrand.id}`;
                    if (logoFile || removeLogo) {
                        // FormData ile logo güncelle
                        const updateFormData = new FormData();
                        updateFormData.append('name', formData.name);
                        if (logoFile) {
                            updateFormData.append('logo', logoFile);
                        }
                        if (removeLogo) {
                            updateFormData.append('remove_logo', 'true');
                        }
                        response = await apiCall(endpoint, 'post', updateFormData);
                    } else {
                        response = await apiCall(endpoint, 'post', { name: formData.name });
                    }
                    break;

                case 'createModel':
                    endpoint = `admin/vehicle-brands/${selectedBrand.id}/model`;
                    response = await apiCall(endpoint, 'post', {
                        name: formData.name,
                        img_url: formData.img_url || null
                    });
                    break;

                case 'updateModel':
                    endpoint = `admin/vehicle-brands/model/update/${selectedModel.id}`;
                    response = await apiCall(endpoint, 'post', {
                        name: formData.name,
                        img_url: formData.img_url || null
                    });
                    break;

                default:
                    return;
            }

            if (response.status === 'success') {
                toast.success(response.message);
                this.handleCloseModal();
                this.fetchBrands();
                this.fetchStatistics();
            } else {
                toast.error(response.message || 'İşlem başarısız');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Bir hata oluştu: ' + (error.response?.data?.message || error.message));
        } finally {
            this.setState({ processingAction: false });
        }
    };

    handleDeleteBrand = async (brand) => {
        const modelCount = brand.models?.length || 0;
        const result = await Swal.fire({
            title: 'Markayı Silmek İstediğinize Emin misiniz?',
            text: modelCount > 0
                ? `Bu marka ve bağlı ${modelCount} model silinecektir. Bu işlem geri alınamaz!`
                : 'Bu işlem geri alınamaz!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Evet, sil!',
            cancelButtonText: 'Hayır, iptal et'
        });

        if (result.isConfirmed) {
            try {
                const response = await apiCall(`admin/vehicle-brands/delete/${brand.id}`, 'delete');

                if (response.status === 'success') {
                    toast.success('Marka başarıyla silindi');
                    this.fetchBrands();
                    this.fetchStatistics();
                } else {
                    toast.error(response.message || 'Silme işlemi başarısız');
                }
            } catch (error) {
                console.error('Error:', error);
                toast.error('Bir hata oluştu: ' + (error.response?.data?.message || error.message));
            }
        }
    };

    handleDeleteModel = async (model) => {
        const result = await Swal.fire({
            title: 'Modeli Silmek İstediğinize Emin misiniz?',
            text: 'Bu işlem geri alınamaz!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Evet, sil!',
            cancelButtonText: 'Hayır, iptal et'
        });

        if (result.isConfirmed) {
            try {
                const response = await apiCall(`admin/vehicle-brands/model/delete/${model.id}`, 'delete');

                if (response.status === 'success') {
                    toast.success('Model başarıyla silindi');
                    this.fetchBrands();
                    this.fetchStatistics();
                } else {
                    toast.error(response.message || 'Silme işlemi başarısız');
                }
            } catch (error) {
                console.error('Error:', error);
                toast.error('Bir hata oluştu: ' + (error.response?.data?.message || error.message));
            }
        }
    };

    toggleBrandExpand = (brandId) => {
        this.setState(prevState => ({
            expandedBrands: {
                ...prevState.expandedBrands,
                [brandId]: !prevState.expandedBrands[brandId]
            }
        }));
    };

    getFilteredBrands = () => {
        const { brands, searchQuery } = this.state;
        if (!searchQuery.trim()) return brands;

        const query = searchQuery.toLowerCase();
        return brands.filter(brand => {
            const brandMatch = brand.name.toLowerCase().includes(query);
            const modelMatch = brand.models?.some(model =>
                model.name.toLowerCase().includes(query)
            );
            return brandMatch || modelMatch;
        });
    };

    getModalTitle = () => {
        const { modalType, selectedBrand } = this.state;
        switch (modalType) {
            case 'createBrand': return 'Yeni Marka Ekle';
            case 'updateBrand': return 'Markayı Düzenle';
            case 'createModel': return `${selectedBrand?.name} - Yeni Model Ekle`;
            case 'updateModel': return `Model Düzenle`;
            default: return '';
        }
    };

    render() {
        const {
            loading,
            statistics,
            searchQuery,
            showModal,
            modalType,
            formData,
            expandedBrands,
            processingAction,
            logoPreview,
            selectedBrand,
        } = this.state;

        const filteredBrands = this.getFilteredBrands();
        const isModelModal = modalType === 'createModel' || modalType === 'updateModel';
        const isBrandModal = modalType === 'createBrand' || modalType === 'updateBrand';

        return (
            <div
                className="vehicle-brand-management"
                onClick={() => {
                    document.body.classList.remove('offcanvas-active');
                }}
            >
                <div className="page-loader-wrapper" style={{ display: loading ? 'block' : 'none' }}>
                    <div className="loader" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div className="m-t-30">
                            <img src={require('../../assets/images/logo-white.png')} width="150" alt="ServiceZone" />
                        </div>
                        <p>Yükleniyor</p>
                        <ReactLoading type="spin" width={'24px'} height={'24px'} color="#ffffff" />
                    </div>
                </div>

                <div className="container-fluid">
                    <PageHeader
                        HeaderText="Araç Marka & Model Yönetimi"
                        Breadcrumb={[
                            { name: 'Admin', navigate: '' },
                            { name: 'Marka & Model', navigate: '' },
                        ]}
                    />

                    {/* Statistics Cards */}
                    <div className="stats-container">
                        <div className="stat-card brands">
                            <h3>Toplam Marka</h3>
                            <p className="value">{statistics.total_brands}</p>
                        </div>
                        <div className="stat-card models">
                            <h3>Toplam Model</h3>
                            <p className="value">{statistics.total_models}</p>
                        </div>
                        <div className="stat-card logos">
                            <h3>Logolu Marka</h3>
                            <p className="value">{statistics.brands_with_logos || 0}</p>
                        </div>
                        <div className="stat-card images">
                            <h3>Görselli Model</h3>
                            <p className="value">{statistics.models_with_images}</p>
                        </div>
                    </div>

                    {/* Search and Actions */}
                    <div className="actions-bar">
                        <div className="search-box">
                            <i className="fa fa-search"></i>
                            <input
                                type="text"
                                placeholder="Marka veya model ara..."
                                value={searchQuery}
                                onChange={(e) => this.setState({ searchQuery: e.target.value })}
                            />
                        </div>
                        <button className="add-brand-btn" onClick={this.handleOpenCreateBrandModal}>
                            <i className="fa fa-plus"></i>
                            Yeni Marka Ekle
                        </button>
                    </div>

                    {/* Brands Grid */}
                    <div className="brands-container">
                        {filteredBrands.length > 0 ? (
                            filteredBrands.map((brand) => {
                                const isExpanded = expandedBrands[brand.id];
                                const modelCount = brand.models?.length || 0;

                                return (
                                    <div key={brand.id} className="brand-card">
                                        <div className="brand-header">
                                            <div className="brand-info">
                                                {brand.logo_url ? (
                                                    <img
                                                        src={brand.logo_url}
                                                        alt={brand.name}
                                                        className="brand-logo"
                                                    />
                                                ) : (
                                                    <div className="brand-icon">
                                                        {brand.name.charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <div>
                                                    <h4 className="brand-name">{brand.name}</h4>
                                                    <p className="brand-count">{modelCount} Model</p>
                                                </div>
                                            </div>
                                            <div className="brand-actions">
                                                <button
                                                    className="add-model-btn"
                                                    onClick={() => this.handleOpenCreateModelModal(brand)}
                                                    title="Model Ekle"
                                                >
                                                    <i className="fa fa-plus"></i>
                                                </button>
                                                <button
                                                    className="edit-btn"
                                                    onClick={() => this.handleOpenUpdateBrandModal(brand)}
                                                    title="Düzenle"
                                                >
                                                    <i className="fa fa-edit"></i>
                                                </button>
                                                <button
                                                    className="delete-btn"
                                                    onClick={() => this.handleDeleteBrand(brand)}
                                                    title="Sil"
                                                >
                                                    <i className="fa fa-trash"></i>
                                                </button>
                                            </div>
                                        </div>

                                        {modelCount > 0 && (
                                            <>
                                                <button
                                                    className={`toggle-models-btn ${isExpanded ? 'expanded' : ''}`}
                                                    onClick={() => this.toggleBrandExpand(brand.id)}
                                                >
                                                    <i className="fa fa-chevron-down"></i>
                                                    {isExpanded ? 'Modelleri Gizle' : `${modelCount} Model Göster`}
                                                </button>

                                                {isExpanded && (
                                                    <div className="models-list">
                                                        {brand.models.map((model) => (
                                                            <div key={model.id} className="model-item">
                                                                <div className="model-info">
                                                                    {model.img_url ? (
                                                                        <img src={model.img_url} alt={model.name} className="model-image" />
                                                                    ) : (
                                                                        <div className="model-image-placeholder">
                                                                            <i className="fa fa-car"></i>
                                                                        </div>
                                                                    )}
                                                                    <span className="model-name">{model.name}</span>
                                                                </div>
                                                                <div className="model-actions">
                                                                    <button
                                                                        className="edit-btn"
                                                                        onClick={() => this.handleOpenUpdateModelModal(brand, model)}
                                                                        title="Düzenle"
                                                                    >
                                                                        <i className="fa fa-edit"></i>
                                                                    </button>
                                                                    <button
                                                                        className="delete-btn"
                                                                        onClick={() => this.handleDeleteModel(model)}
                                                                        title="Sil"
                                                                    >
                                                                        <i className="fa fa-trash"></i>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {modelCount === 0 && (
                                            <div className="no-models">
                                                <i className="fa fa-car"></i>
                                                <p>Henüz model eklenmemiş</p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="col-12">
                                <div className="empty-state">
                                    <i className="fa fa-car"></i>
                                    <h3>Marka Bulunamadı</h3>
                                    <p>
                                        {searchQuery
                                            ? 'Arama kriterlerine uygun marka bulunamadı.'
                                            : 'Henüz marka eklenmemiş.'}
                                    </p>
                                    {!searchQuery && (
                                        <button className="add-brand-btn" onClick={this.handleOpenCreateBrandModal}>
                                            <i className="fa fa-plus"></i>
                                            İlk Markayı Ekle
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal for Create/Update Brand or Model */}
                {showModal && (
                    <div className="modal-overlay" onClick={this.handleCloseModal}>
                        <div className="brand-modal" onClick={(e) => e.stopPropagation()}>
                            <button className="modal-close" onClick={this.handleCloseModal}>
                                ×
                            </button>

                            <h2 className="modal-title">{this.getModalTitle()}</h2>

                            <form onSubmit={this.handleSubmit} className="brand-form">
                                {/* Logo Upload (only for brand modals) */}
                                {isBrandModal && (
                                    <div className="form-group logo-upload-container">
                                        <label>Marka Logosu</label>
                                        <input
                                            type="file"
                                            ref={this.logoInputRef}
                                            accept="image/jpeg,image/jpg,image/png,image/svg+xml,image/webp"
                                            onChange={this.handleLogoSelect}
                                            className="logo-upload-input"
                                        />
                                        <div
                                            className={`logo-upload-area ${logoPreview ? 'has-logo' : ''}`}
                                            onClick={this.triggerLogoInput}
                                        >
                                            {logoPreview ? (
                                                <div className="logo-preview-container">
                                                    <img src={logoPreview} alt="Logo Preview" className="logo-preview" />
                                                    <div className="logo-actions">
                                                        <button
                                                            type="button"
                                                            className="change-btn"
                                                            onClick={(e) => { e.stopPropagation(); this.triggerLogoInput(); }}
                                                        >
                                                            <i className="fa fa-refresh"></i> Değiştir
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="remove-btn"
                                                            onClick={(e) => { e.stopPropagation(); this.handleRemoveLogo(); }}
                                                        >
                                                            <i className="fa fa-trash"></i> Kaldır
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="logo-upload-placeholder">
                                                    <i className="fa fa-cloud-upload"></i>
                                                    <span>Logo yüklemek için tıklayın</span>
                                                    <small>JPG, PNG, SVG veya WebP (max 2MB)</small>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="form-group">
                                    <label>{isModelModal ? 'Model Adı' : 'Marka Adı'} *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={this.handleInputChange}
                                        placeholder={isModelModal ? 'örn: 3 Serisi' : 'örn: BMW'}
                                        required
                                        autoFocus
                                    />
                                </div>

                                {isModelModal && (
                                    <div className="form-group">
                                        <label>Görsel URL (Opsiyonel)</label>
                                        <input
                                            type="text"
                                            name="img_url"
                                            value={formData.img_url}
                                            onChange={this.handleInputChange}
                                            placeholder="https://example.com/image.jpg"
                                        />
                                    </div>
                                )}

                                <div className="modal-actions">
                                    <button type="button" className="btn-cancel" onClick={this.handleCloseModal}>
                                        İptal
                                    </button>
                                    <button type="submit" className="btn-submit" disabled={processingAction}>
                                        {processingAction ? (
                                            <>
                                                <ReactLoading type="spin" color="#fff" width={20} height={20} />
                                                <span>İşleniyor...</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-save"></i>
                                                {modalType.includes('create') ? 'Oluştur' : 'Güncelle'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps)(VehicleBrandManagement);
